import type { FC } from 'react';
import { useState } from 'react';

import { useCms, useContent } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import AddressFinderWidget, {
  addressFinderSchema,
  defaultValues as addressFinderDefaultValues,
} from '@eneco/components/AddressFinder/AddressFinder';
import { useAddressFinderQueryStringDefaultValues } from '@eneco/components/AddressFinder/hooks/useAddressFinderQueryStringDefaultValues';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { useIsLargeBusinessCheck } from '@eneco/hooks/address';
import { typedYupResolver } from '@eneco/hooks/form';
import { useRedirect } from '@eneco/hooks/redirect';
import { Box, Button, Form, Stack } from '@eneco/sparky';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { toAddressFinderCustomLabels } from './address-finder-custom-labels';
import { findByRole } from './AddressFinder';
import { EnergyUsagePurposeSelector } from './EnergyUsagePurposeSelector';
import type { SmeAddressFinderContent } from './sme-address-finder-content.types';

interface FormProps {
  city?: string;
  houseNumber?: string;
  houseNumberSuffix?: string;
  postalCode?: string;
  street?: string;
  energyUsagePurpose?: string;
}

/**
 * `SmeAddressFinder` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy Sitecore lane
 * (`libs/sitecore/components/content/src/AddressFinder/SmeAddressFinder.tsx`) — the MKB variant of
 * `AddressFinder`, adding: a redirect to `energyUsagePurposeRedirectLink` when "home" is selected instead
 * of "business" (`AddressFinder` uses the opposite value for the same idea — a pre-existing content-model
 * inconsistency between the two, not something to reconcile here); and, on submit with "business" still
 * selected, an `useIsLargeBusinessCheck` (`@eneco/hooks/address`, DC-backed) address lookup that — when
 * the address resolves to a large business — interposes an `enterpriseDialog` confirmation instead of
 * submitting immediately, redirecting to `enterpriseRedirectLink` on confirm.
 *
 * Known simplification: the legacy component also calls `authenticateUser` (`@eneco/customer-insights`)
 * on enterprise-redirect confirm, seeding Microsoft Customer Insights' personalization with the entered
 * postal code. `lib:customer-insights` is not in `lib:cms-components`'s allowed dependencies
 * (`eslint.config.mjs`), the same class of trade `NotificationBox.tsx` documents for dropping its view
 * tracking — the redirect itself is unaffected, only that downstream personalization signal is lost.
 */
const SmeAddressFinder: FC = () => {
  const { adapter } = useCms();
  const content = useContent<SmeAddressFinderContent>();
  const addressFinderQueryStringDefaultValues = useAddressFinderQueryStringDefaultValues();
  const redirect = useRedirect();
  const [isSuffixRequired, setIsSuffixRequired] = useState(false);

  const houseNumberFormField = findByRole(content.formFields, 'houseNumberFormField') ?? content.houseNumberFormField;
  const houseNumberSuffixFormField =
    findByRole(content.formFields, 'houseNumberSuffixFormField') ?? content.houseNumberSuffixFormField;
  const postalCodeFormField = findByRole(content.formFields, 'postalCodeFormField') ?? content.postalCodeFormField;
  const notFoundErrorNotification =
    findByRole(content.notifications, 'notFoundErrorNotification') ?? content.notFoundErrorNotification;
  const serverErrorNotification =
    findByRole(content.notifications, 'serverErrorNotification') ?? content.serverErrorNotification;
  const energyUsagePurposeList =
    findByRole(content.radioGroups, 'energyUsagePurposeList') ?? content.energyUsagePurposeList;
  const enterpriseDialog = findByRole(content.buttonDialogs, 'enterpriseDialog') ?? content.enterpriseDialog;

  const customLabels = toAddressFinderCustomLabels(adapter, {
    houseNumberFormField,
    houseNumberSuffixFormField,
    postalCodeFormField,
    notFoundErrorNotification,
    serverErrorNotification,
  });

  // The Sitecore lane's `energyUsagePurposeList` never carries a `requiredMessage`
  // (`libs/sitecore/types/SmeAddressFinder.d.ts`) — the legacy component hard-codes its own validation
  // message instead of reading one from content. Falling back to that same literal, rather than gating
  // the whole validator on `requiredMessage` being present, keeps the Sitecore lane rejecting `'none'`
  // the way the legacy component always does.
  const formSchema = yup.object({
    ...addressFinderSchema(isSuffixRequired).fields,
    ...(energyUsagePurposeList
      ? {
          energyUsagePurpose: yup
            .string()
            .required(energyUsagePurposeList.requiredMessage ?? "Maak een keuze tussen 'Huishouden' of 'Zakelijk'")
            .notOneOf(['none'], energyUsagePurposeList.requiredMessage ?? "Maak een keuze tussen 'Huishouden' of 'Zakelijk'"),
        }
      : {}),
  });

  const formMethods = useForm<FormProps>({
    defaultValues: {
      ...addressFinderDefaultValues,
      ...addressFinderQueryStringDefaultValues,
      energyUsagePurpose: content.energyUsagePurposeDefaultOption ?? 'none',
    },
    mode: 'onBlur',
    resolver: typedYupResolver<FormProps>(formSchema),
  });

  const { handleSubmit, control, watch } = formMethods;
  const { energyUsagePurpose, postalCode, houseNumber, houseNumberSuffix } = watch();
  const isLargeBusiness = useIsLargeBusinessCheck({ postalCode, houseNumber, houseNumberSuffix });

  const buttonLink = linkWithUrl(content.buttonLink);
  const energyUsagePurposeRedirectLink = linkWithUrl(content.energyUsagePurposeRedirectLink);
  const enterpriseRedirectLink = linkWithUrl(content.enterpriseRedirectLink);

  const redirectTo = (link: typeof buttonLink, params: Pick<FormProps, 'postalCode' | 'houseNumber' | 'houseNumberSuffix'>) => {
    redirect({
      queryParams: {
        postalCode: params.postalCode?.replace?.(' ', ''),
        houseNumber: params.houseNumber,
        houseNumberSuffix: params.houseNumberSuffix,
      },
      // Force a full page reload rather than client-side routing — required when redirecting between
      // different sites (e.g. MKB to Grootzakelijk).
      link: link?.href ? { href: link.href, linktype: 'external', target: link.target ?? '', querystring: '', anchor: '' } : undefined,
    });
  };

  const onSubmit = (values: FormProps) => {
    const linkToUse = energyUsagePurpose === 'home' && energyUsagePurposeRedirectLink?.href ? energyUsagePurposeRedirectLink : buttonLink;
    redirectTo(linkToUse, values);
  };

  const handleEnterpriseRedirect = (values: FormProps) => redirectTo(enterpriseRedirectLink, values);

  return (
    <FormProvider {...formMethods}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <AddressFinderWidget
          customLabels={customLabels}
          isSuffixRequired={isSuffixRequired}
          setIsSuffixRequired={setIsSuffixRequired}
        />

        {energyUsagePurposeList && (
          <EnergyUsagePurposeSelector<FormProps>
            control={control}
            label={energyUsagePurposeList.label ?? ''}
            options={energyUsagePurposeList.options ?? []}
            direction="row"
          />
        )}

        {buttonLink?.href && (
          <Box paddingTop="6">
            {isLargeBusiness ? (
              <TrackedDialog
                title={enterpriseDialog?.title}
                trigger={
                  <Button size="regular" type="button">
                    {buttonLink.text}
                  </Button>
                }
                width="regular">
                <Box paddingBottom="6">
                  <RichText html={adapter.toRichText(enterpriseDialog?.content)} />
                </Box>
                <Stack direction="row" gap={5}>
                  <Button
                    action="primary"
                    type="button"
                    size="regular"
                    onClick={() => handleEnterpriseRedirect({ postalCode, houseNumber, houseNumberSuffix })}>
                    {enterpriseDialog?.submitButtonText}
                  </Button>
                  <Button
                    action="secondary"
                    size="regular"
                    type="button"
                    onClick={() => onSubmit({ postalCode, houseNumber, houseNumberSuffix, energyUsagePurpose })}>
                    {enterpriseDialog?.cancelButtonText}
                  </Button>
                </Stack>
              </TrackedDialog>
            ) : (
              <Button size="regular" type="submit">
                {buttonLink.text}
              </Button>
            )}
          </Box>
        )}
      </Form>
    </FormProvider>
  );
};

export default SmeAddressFinder;
